import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSupervisorAuth } from '../context/SupervisorAuthContext';
import './SupervisorLayout.css';

// Liens directs (pas de sous-menu) + 2 groupes deroulants (Controles, Rapports)
// — avant, un seul <nav> plat a 17 liens, illisible/casse en mobile. Meme
// pattern d'interaction que MainNav.jsx (portail company) : clic/tap pour
// ouvrir/fermer, ferme au clic en dehors (pas de hover, deja identifie comme
// non fiable au toucher sur MainNav).
const directLinks = [
  { label: 'Dashboard', path: '/supervisor/dashboard' },
  { label: 'Mes Banks', path: '/supervisor/banks' },
  { label: 'Sous-superviseurs', path: '/supervisor/sub-supervisors' },
];

const dropdownGroups = {
  // Memes URLs que la grille d'icones de SupervisorCentralPage.jsx — demande
  // explicite 2026-09-16 ("ce sera ici et dans le navbar aussi les urls").
  Central: [
    { label: 'Suivi des ventes', path: '/supervisor/sales-monitoring' },
    { label: 'Banques Connecte', path: '/supervisor/banques-connecte' },
    { label: 'Verifier le Ticket', path: '/supervisor/verifier-ticket' },
    // Reutilisent des pages deja existantes ailleurs (Rapports) — lecture
    // seule, meme decision de portee que le reste (2026-09-17).
    { label: 'Numeros gagnants', path: '/supervisor/winning-numbers' },
    { label: 'Fermer Vente', path: '/supervisor/close-sales' },
    { label: 'Voir Parametres', path: '/supervisor/see-parameters' },
    { label: 'Suivi Express', path: '/supervisor/express-monitoring' },
    { label: 'Changer mot de passe POS', path: '/supervisor/change-terminal-password' },
    { label: 'Annuler une fiche', path: '/supervisor/nullify-ticket' },
    { label: 'Retablir une fiche annulee', path: '/supervisor/revert-nullified-ticket' },
    { label: 'Payer une fiche', path: '/supervisor/pay-ticket' },
  ],
  Controles: [
    { label: 'Parametres de loterie', path: '/supervisor/product-configs' },
    { label: 'Limites par numero', path: '/supervisor/number-limits' },
  ],
  // Les 12 memes rapports que le menu company (menu_catalog.py) — tous gates
  // par SupervisorMenuPermission (2026-09-17), rien accorde par defaut a un
  // nouveau superviseur.
  Rapports: [
    { label: 'Rapport general', path: '/supervisor/general-report' },
    { label: 'Liste des ventes', path: '/supervisor/sales-list' },
    { label: 'Numeros gagnants', path: '/supervisor/winning-numbers' },
    { label: 'Tickets Gagnant', path: '/supervisor/winning-tickets' },
    { label: 'Resume accumule', path: '/supervisor/accumulated-summary' },
    { label: 'Cumul detaille', path: '/supervisor/cumul-detaille' },
    { label: 'Tickets Vendus', path: '/supervisor/tickets-vendus' },
    { label: 'Tickets Annulaient', path: '/supervisor/tickets-annules' },
    { label: 'Resume de la Banque-Lottery', path: '/supervisor/resume-banque-lottery' },
    { label: 'Paris gagnants', path: '/supervisor/prized-numbers' },
    { label: 'Rapport du Terminal', path: '/supervisor/terminal-report' },
    { label: 'Rapport du Banque', path: '/supervisor/bank-report' },
  ],
  // Banque + Act/Desactiver Ventes Manuel seulement — Message et Marriage
  // Gratis (Bonos) jamais accordables a un superviseur (champs company-wide,
  // decision explicite 2026-09-17), donc absents ici et du catalogue.
  Parametres: [
    { label: 'Banque', path: '/supervisor/banque' },
    { label: 'Act/Desactiver Ventes (Manuel)', path: '/supervisor/act-desactiver-ventes-manuel' },
  ],
};

// Central/Rapports/Controles/Parametres sont gates par SupervisorMenuPermission
// (2026-09-17, "rien par defaut, la company accorde") — les liens directs
// (Dashboard/Mes Banks/Sous-superviseurs) restent hors de ce systeme pour
// l'instant (pas dans le catalogue), donc toujours visibles. Un groupe dont
// tous les items sont retires disparait entierement (pas de toggle vide).
const filterDropdownGroups = (menuKeys) => {
  const granted = new Set(menuKeys || []);
  const gatedGroups = new Set(['Central', 'Rapports', 'Controles', 'Parametres']);
  return Object.fromEntries(
    Object.entries(dropdownGroups)
      .map(([group, items]) => [
        group,
        gatedGroups.has(group) ? items.filter((item) => granted.has(item.path)) : items,
      ])
      .filter(([, items]) => items.length > 0)
  );
};

const SupervisorLayout = ({ children }) => {
  const { supervisor, logout } = useSupervisorAuth();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const navRef = useRef(null);
  const visibleGroups = filterDropdownGroups(supervisor?.menu_keys);

  useEffect(() => {
    if (!openMenu) return undefined;
    const handleOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [openMenu]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="sv-layout">
      <div className="sv-topbar">
        <span className="sv-brand">
          <img className="sv-logo" src="/logo-cgl-transparent.png" alt="CGL Multi-services" />
          Superviseur
        </span>
        <div className="sv-topbar-right">
          {supervisor && <span className="sv-username">{supervisor.display_name || supervisor.name}</span>}
          <span className="sv-logout" onClick={handleLogout}>Deconnexion</span>
        </div>
      </div>
      <nav className="sv-nav" ref={navRef}>
        <div className="sv-nav-scroll">
          {directLinks.map((item) => (
            <Link key={item.path} to={item.path} className="sv-nav-link">{item.label}</Link>
          ))}
          {Object.keys(visibleGroups).map((group) => (
            <div key={group} className="sv-nav-item">
              <span
                className="sv-nav-link sv-nav-toggle"
                onClick={() => setOpenMenu((cur) => (cur === group ? null : group))}
              >
                {group}
              </span>
              {openMenu === group && (
                <div className="sv-dropdown sv-dropdown-desktop">
                  {visibleGroups[group].map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="sv-dropdown-link"
                      onClick={() => setOpenMenu(null)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Copie mobile du dropdown ouvert, hors du wrapper overflow-x:auto
            ci-dessus : sur mobile, overflow-x:auto seul fait aussi clipper
            l'axe Y (regle CSS : l'autre axe 'visible' devient 'auto'), donc un
            dropdown imbrique dedans etait ouvert (state OK) mais invisible,
            ecrase a la hauteur de la barre de nav. Affichee/masquee uniquement
            via media query (SupervisorLayout.css), le JSX reste toujours dans
            le DOM. */}
        {openMenu && visibleGroups[openMenu] && (
          <div className="sv-dropdown sv-dropdown-mobile">
            {visibleGroups[openMenu].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="sv-dropdown-link"
                onClick={() => setOpenMenu(null)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </nav>
      <div className="sv-body">{children}</div>
    </div>
  );
};

export default SupervisorLayout;
